import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetEstablishments } from '@/common/integrations/menhir/employee/establishments/establishments.hooks';
import {
  IEstablishment,
  IEstablishmentItem,
} from '@/common/integrations/menhir/employee/establishments/establishments.types';
import { findEstablishment } from '@/common/integrations/menhir/employee/establishments/establishments.utils';
import { useGetVouchers } from '@/common/integrations/menhir/employee/vouchers/vouchers.hooks';
import { IVoucher } from '@/common/integrations/menhir/employee/vouchers/vouchers.types';
import { maybeNavigateBack } from '@/common/utils/router';
import { StringUtils } from '@/common/utils/string';
import { ComboboxFieldAddSchoolAppendix } from '@/web/components/ComboboxFieldAddSchoolAppendix/ComboboxFieldAddSchoolAppendix';
import { ControlledCheckbox } from '@/web/components/Fields/ControlledCheckbox/ControlledCheckbox';
import { ControlledComboboxField } from '@/web/components/Fields/ControlledComboboxField/ControlledComboboxField';
import { ControlledMoneyField } from '@/web/components/Fields/ControlledMoneyField/ControlledMoneyField';
import { ControllerContext } from '@/web/components/Fields/ControllerContext/ControllerContext';
import { FloatingFooter } from '@/web/components/FloatingFooter/FloatingFooter';
import { LoadingPlaceholder } from '@/web/components/LoadingPlaceholder/LoadingPlaceholder';
import {
  TVoucherFormSchema,
  defaultVoucherSchema,
  voucherSchemaWithChildAgeConfirmation,
} from '@/web/containers/Benefits/SubmitChildcareVoucher/components/VoucherForm/VoucherForm.schema';
import { minColumnWidthForm } from '@/web/styles/css-vars';
import { Button, Flex, Grid, Label, Text } from '@coverflex-tech/hypernova';
import { TMoney } from '@coverflex-tech/hypernova-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'expo-router';

export interface IVoucherData {
  amount: TMoney;
  establishment: IEstablishment;
}

interface IProps {
  initialVoucher?: IVoucher;
  balance: TMoney;
  onSubmit: (form: IVoucherData) => void;
  setStep: (step: number) => void;
  childAgeConfirmation?: { childName: string; maxChildAge: number };
}

export const VoucherForm = ({
  initialVoucher,
  balance,
  onSubmit,
  setStep,
  childAgeConfirmation,
}: IProps) => {
  const { t } = useTranslation();
  const { establishments } = useGetEstablishments(['childcare']);
  const { vouchers } = useGetVouchers('childcare');
  const router = useRouter();

  const methods = useForm<TVoucherFormSchema>({
    mode: 'onTouched',
    defaultValues: {
      amount: initialVoucher?.amount,
      availableAmount: balance,
      establishment: initialVoucher?.establishment,
    },
    resolver: zodResolver(
      childAgeConfirmation
        ? voucherSchemaWithChildAgeConfirmation
        : defaultVoucherSchema
    ),
  });

  const handleSubmit = async ({
    amount,
    establishment,
  }: TVoucherFormSchema) => {
    onSubmit({
      amount,
      establishment,
    });
    setStep(1);
  };

  const schools: IEstablishmentItem[] = useMemo(() => {
    if (!vouchers || !establishments) {
      return [];
    }

    const uniqueRecentSchools = vouchers
      .map((voucher) => ({ ...voucher.establishment, suggested: true }))
      .filter(
        (school, index, schools) =>
          schools.findIndex((recentSchool) => recentSchool.id === school.id) ===
          index
      );
    return [
      ...uniqueRecentSchools,
      ...establishments.filter(
        (school) =>
          !uniqueRecentSchools.find(
            (recentSchool) => recentSchool.id === school.id
          )
      ),
    ];
  }, [establishments, vouchers]);

  if (!establishments || !vouchers) {
    return <LoadingPlaceholder />;
  }

  return (
    <FormProvider {...methods}>
      <Grid.Root auto minColumnWidth={minColumnWidthForm}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <Flex flexDirection="column" gap="md">
            <ControlledMoneyField<TVoucherFormSchema>
              name="amount"
              label={t('pocket_amount_field.amount')}
              availableAmount={balance}
              availableLabel={t('pocket_amount_field.balance')}
            />
            <ControlledComboboxField<TVoucherFormSchema, IEstablishment>
              name="establishment"
              label={t(
                'dashboard.submit_childcare_voucher.fields.childcare.label'
              )}
              options={schools}
              itemToString={(item) => item?.name ?? ''}
              itemMatcher={findEstablishment}
              labels={{
                noResults: t('form.search.no_results'),
              }}
              renderItem={({ item }) => (
                <div>
                  <Label bold>{item.name}</Label>
                  <Text color="neutral20" size="sm">
                    {StringUtils.buildAddressString(item.address)}
                  </Text>
                </div>
              )}
              appendix={<ComboboxFieldAddSchoolAppendix />}
            />
            {childAgeConfirmation && (
              <ControlledCheckbox<TVoucherFormSchema>
                name="confirmChildAge"
                variant="descriptive"
                label={t(
                  'dashboard.submit_childcare_voucher.fields.es.confirm_child_age.label',
                  {
                    name: childAgeConfirmation.childName,
                    age: childAgeConfirmation.maxChildAge,
                  }
                )}
              />
            )}
          </Flex>

          <ControllerContext
            render={({ formState: { isSubmitting } }) => {
              return (
                <FloatingFooter>
                  <Flex gap="xl">
                    <Button
                      variant="tertiary"
                      onClick={() => maybeNavigateBack(router)}
                    >
                      {t('dashboard.submit_childcare_voucher.cancel_label')}
                    </Button>
                    <Button type="submit" loading={isSubmitting}>
                      {t('dashboard.submit_childcare_voucher.continue_label')}
                    </Button>
                  </Flex>
                </FloatingFooter>
              );
            }}
          />
        </form>
      </Grid.Root>
    </FormProvider>
  );
};
